/**
 * The link/reauth funnel's pure helpers — split out of `useAggregator.ts`
 * (non-blocking #4, architect review, #399): `LINK_ABANDON_REASONS`,
 * `LinkAbortError`, `classifyLinkAbandonReason` and `accountsAddedCountOf`
 * have zero dependency on React, TanStack Query or any hook, so they belong
 * beside the funnel's data shapes (`providers/types.ts`) rather than inside
 * the 700-line file of hooks that merely call them.
 */

/**
 * `link_abandoned`'s `reason_code` — iOS's own ten values, verbatim (#399:
 * "add cases, never rename" — a stable, shared taxonomy, not a coupling).
 * Not every value is reachable from THIS client's own link flow today —
 * `provider_payload_mismatch` and `session_expired` have no source of
 * evidence here yet — and the type stays complete anyway, because it
 * describes the shared vocabulary rather than this file's own coverage.
 */
export const LINK_ABANDON_REASONS = [
  'user_cancelled',
  'presentation_unavailable',
  'provider_payload_mismatch',
  'callback_malformed',
  'provider_failure',
  'unsupported_by_provider',
  'backend',
  'mfa_required',
  'session_expired',
  'unknown',
] as const
export type LinkAbandonReason = (typeof LINK_ABANDON_REASONS)[number]

/**
 * What a `LinkAdapter` throws — or rejects `open()`/`redirectFlow.resume()`
 * with — when it cannot complete. Carries the REASON as a structured field
 * alongside the user-facing `message`, so `classifyLinkAbandonReason` below
 * reads a fact the adapter already knows rather than pattern-matching prose
 * two modules away.
 *
 * **B2, architect review (#399).** An earlier version of the classifier
 * `switch`ed on the literal user-facing strings `plaid.ts`/`flinks.ts`
 * produce, and its own docblock claimed a wording change on either side
 * would be caught by a test on the other — checked against the real test
 * files and false: `plaid.test.ts` asserts against its OWN literal,
 * `useAggregator.test.tsx`'s classifier tests re-typed a COPY of the same
 * literal, and neither file imports the other, so rewording
 * `GENERIC_PLAID_ERROR` (`plaid.ts`) reds `plaid.test.ts` alone while every
 * real Plaid failure goes on silently reporting `'unknown'` forever. The
 * fix is not a better regex or an exported constant to import — it is
 * attaching the reason at the one place that actually knows it: inside the
 * adapter, at the moment it decides why the flow failed. `LinkAbandonReason`
 * is a shared, provider-agnostic taxonomy by construction (the whole point
 * of this file), so an adapter naming one is not the provider-name coupling
 * `registry.ts`'s "no call site outside this file compares a provider name"
 * rules out — that rule is about the two provider-name LITERALS, not about
 * a bounded, cross-provider enum every adapter already has to pick from.
 */
export class LinkAbortError extends Error {
  readonly reason: LinkAbandonReason

  constructor(reason: LinkAbandonReason, message: string) {
    super(message)
    this.name = 'LinkAbortError'
    this.reason = reason
  }
}

/**
 * `link_abandoned`'s `reason_code` — read STRUCTURALLY off what the adapter
 * attached, never by matching a user-facing sentence written for a human.
 *
 * `'unknown'` for anything that is not a `LinkAbortError` this function
 * recognises — including a plain `Error` some other code path threw, and a
 * non-`Error` throw — never a specific-sounding default. A classifier whose
 * whole job is admitting what it cannot tell must not be able to produce a
 * confident wrong answer for an input it was never shown.
 *
 * `AggregatorError` (`useAggregator.ts`'s own request failures) and
 * `mfa_required` (the settled `result.status === 'mfa_required'` branch) are
 * deliberately NOT read here — both are facts the two `useLinkFlow`/
 * `useReauthFlow` call sites already have directly, with nothing to classify.
 */
export function classifyLinkAbandonReason(cause: unknown): LinkAbandonReason {
  return cause instanceof LinkAbortError ? cause.reason : 'unknown'
}

/**
 * The number of accounts THIS link added — read off Plaid's own `onSuccess`
 * metadata (`metadata.accounts: PlaidAccount[]`, `react-plaid-link`'s own
 * type, checked against `node_modules/react-plaid-link/dist/types/index.d
 * .ts`), which `plaid.ts`'s `onSuccess` passes straight through untouched as
 * `CompletionPayload.metadata`.
 *
 * `null` — never a fabricated `1` — for Flinks, whose metadata is
 * `FlinksCallback.tsx`'s raw redirect query parameters and carries no such
 * field, and for anything else that does not match this one shape. Same
 * "read defensively, refuse to guess" discipline `transactionCountOf`/
 * `namesReauthRequired` (`useAggregator.ts`) already apply.
 */
export function accountsAddedCountOf(metadata: Record<string, unknown>): number | null {
  const accounts = metadata.accounts
  return Array.isArray(accounts) ? accounts.length : null
}
